import { Dispatch, useCallback, useState } from "react";
import { Button, Modal, Radio, Row, Typography } from "antd";
import { RateNegotiationConfigEnum } from "src/components/RateNegotiationModal/interfaces";
import { closeOpenNegotiationsForFacility } from "src/api/rateNegotiations";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { logEvent } from "@src/appV2/lib/analytics";
import { ADMIN_EVENTS } from "src/constants/firebaseEvents";

const { Text } = Typography;

export interface IEditStatusModal {
  title: JSX.Element;
  visible: boolean;
  setVisibility: Dispatch<React.SetStateAction<boolean>>;
  currentStatus: RateNegotiationConfigEnum | undefined;
  updateFacilityData: (data: { rateNegotiation: RateNegotiationConfigEnum }) => Promise<void>;
  facilityId: string;
}

export function EditStatusModal({
  title,
  visible,
  setVisibility,
  currentStatus,
  updateFacilityData,
  facilityId,
}: IEditStatusModal) {
  const [newStatus, setNewStatus] = useState<RateNegotiationConfigEnum>(
    currentStatus ?? RateNegotiationConfigEnum.OFF
  );
  const [loading, setLoading] = useState<boolean>(false);

  const changeStatus = useCallback(
    async (status: RateNegotiationConfigEnum) => {
      try {
        setLoading(true);
        let countOfNegotiationsClosed = 0;

        if (status === RateNegotiationConfigEnum.OFF) {
          // Negotiations to be closed before updating DB flag.
          // Using `await`, so that if error occurs in this API,
          // the DB flag remains unchanged and user can try again
          countOfNegotiationsClosed = await closeOpenNegotiationsForFacility(facilityId);
          showSuccessToast(`Closed ${countOfNegotiationsClosed ?? 0} negotiations`);
        }
        await updateFacilityData({ rateNegotiation: status });

        // Track Segment event after successful update
        logEvent(ADMIN_EVENTS.RATE_NEGOTIATION_TOGGLED, {
          facilityId,
          previousStatus: currentStatus,
          status,
          source: "admin-panel",
          ...(status === RateNegotiationConfigEnum.OFF && {
            negotiationsClosed: countOfNegotiationsClosed,
          }),
        });

        showSuccessToast(`Successfully updated status to ${status}`);
      } catch (err) {
        showErrorToast(`Failed to update status: ${(err as Error).message}`);
      } finally {
        setLoading(false);
        setVisibility(false);
      }
    },
    [facilityId, updateFacilityData, setVisibility, currentStatus]
  );
  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={() => setVisibility(false)}
      footer={[
        <Row justify="center" key="submit">
          <Button
            data-testid="rate-negotiation-status-card-modal-confirm"
            type="primary"
            loading={loading}
            onClick={() => {
              void changeStatus(newStatus);
            }}
          >
            Submit Rate Negotiation Status
          </Button>
        </Row>,
      ]}
    >
      <Text>
        Setting this to ON (with feature flag enabled) will allow HCPs to propose new rates for all
        open shifts at this workplace.
        <br />
        <br />
        Setting this to OFF (and/or if the feature flag is disabled) will prevent HCPs from
        proposing new rates, and will immediately REJECT and end any negotiations that are currently
        open.
      </Text>
      <br />
      <Radio.Group
        onChange={(event) => {
          setNewStatus(event.target.value);
        }}
        value={newStatus}
      >
        <Radio value={RateNegotiationConfigEnum.ON}>ON</Radio>
        <br />
        <Radio value={RateNegotiationConfigEnum.OFF}>OFF</Radio>
      </Radio.Group>
    </Modal>
  );
}
